import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import useDebounce from '@main-hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { usePromoterRate } from '@efex-components/payables/PaymentModal/components/PromoterRateInput';
import { useRateValidationAlert } from '../../../RateValidationAlert/hooks';
import {
  validateDecimalInput,
  parseValidRate,
  validatePromoterRate,
  getValidPromoterRate,
} from '../../../../utils/promoterRateHelpers';
import {
  UsePromoterRateInputParams,
  UsePromoterRateInputReturn,
} from './usePromoterRateInput.types';

const DEBOUNCE_TIME = 800;

export const usePromoterRateInput = (
  params: UsePromoterRateInputParams
): UsePromoterRateInputReturn => {
  const { t } = useTranslation();
  const {
    exchangeData,
    promoterId,
    enterpriseId,
    currentAmount,
    isReverse,
    rateReference,
    currency,
    clientCurrency,
    onRateChange,
    onRateClear,
    onValidationChange,
  } = params;

  const [rateInput, setRateInput] = useState<string>('');
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [hookError, setHookError] = useState<string | null>(null);
  const isUpdatingFromPromoterRef = useRef(false);

  const debouncedCurrentAmount = useDebounce(currentAmount, DEBOUNCE_TIME);

  const {
    createRateAndCalculate,
    createLoading,
    createData,
    createError,
    clearActivePromoterRate,
    recalculateWithCurrentRate,
    activeRateUuid,
  } = usePromoterRate();

  const baseValidation = useMemo(() => {
    if (!exchangeData) return null;
    return validatePromoterRate(0, exchangeData);
  }, [exchangeData]);

  const baseValidationMessage = useMemo(() => {
    if (!baseValidation) return '';

    return t('rateMustBe', {
      comparison: t(baseValidation.messageParams.comparison),
      rate: baseValidation.messageParams.rate,
    });
  }, [baseValidation, t]);

  const isCurrentRateValid = useMemo(() => {
    if (!exchangeData || !rateInput) return true;
    const rate = parseValidRate(rateInput, exchangeData);
    if (!rate) return false;
    const validation = validatePromoterRate(rate, exchangeData);
    return validation.isValid;
  }, [exchangeData, rateInput]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isCurrentRateValid);
    }
  }, [isCurrentRateValid, onValidationChange]);

  useEffect(() => {
    const shouldAutoCreatePromoterRate =
      exchangeData?.reference &&
      exchangeData?.buy &&
      promoterId &&
      debouncedCurrentAmount &&
      debouncedCurrentAmount !== '0' &&
      !activeRateUuid &&
      !createLoading &&
      !hasUserInteracted &&
      rateReference;

    if (shouldAutoCreatePromoterRate) {
      createRateAndCalculate({
        promoterId,
        originalReference: rateReference,
        promoterRate: getValidPromoterRate(exchangeData),
        currentAmount: debouncedCurrentAmount,
        isReverse,
        enterpriseId,
        onCalculationResult: ({
          calculatedAmount,
          feeDescription,
          activeRateUuid: newActiveRateUuid,
        }) => {
          onRateChange({
            rate: parseFloat(getValidPromoterRate(exchangeData)),
            calculatedAmount: calculatedAmount.toString(),
            isReverse,
            feeDescription,
            activeRateUuid: newActiveRateUuid || activeRateUuid,
          });
        },
        setCancelFunction: () => {},
      }).catch((error) => {
        console.error('Failed to auto-create promoter rate:', error);
        setHookError('Failed to initialize promoter rate');
      });
    }
  }, [
    exchangeData,
    promoterId,
    debouncedCurrentAmount,
    activeRateUuid,
    createLoading,
    hasUserInteracted,
    rateReference,
    isReverse,
    enterpriseId,
    createRateAndCalculate,
    onRateChange,
  ]);

  const { alertProps } = useRateValidationAlert({
    validatePromoterRate,
    finalExchangeRateInput: rateInput,
    hasUserInteractedWithRate: hasUserInteracted,
    isCurrentRateValid,
    exchangeData,
    createLoading,
    createData,
    createError,
    originAmount: currentAmount,
    destinationAmount: currentAmount,
  });

  const handleRateClear = useCallback(async () => {
    if (!exchangeData?.buy) {
      clearActivePromoterRate();

      if (currentAmount && currentAmount !== '0') {
        recalculateWithCurrentRate({
          amount: currentAmount,
          rateUuid: rateReference || '',
          reverse: isReverse,
          enterpriseId,
          requestFee: true,
          currency,
          desCurrency: clientCurrency,
          onChangeResult: ({ amount: result, feeDescription }) => {
            onRateClear({
              calculatedAmount: result.toString(),
              isReverse,
              feeDescription,
              activeRateUuid: null,
            });
          },
          setCancelFunction: () => {},
        });
      }
      return;
    }

    if (currentAmount && currentAmount !== '0' && rateReference) {
      await createRateAndCalculate({
        promoterId,
        originalReference: rateReference,
        promoterRate: getValidPromoterRate(exchangeData),
        currentAmount,
        isReverse,
        enterpriseId,
        onCalculationResult: ({
          calculatedAmount,
          feeDescription,
          activeRateUuid: newActiveRateUuid,
        }) => {
          onRateClear({
            calculatedAmount: calculatedAmount.toString(),
            isReverse,
            feeDescription,
            activeRateUuid: newActiveRateUuid || activeRateUuid,
          });
        },
        setCancelFunction: () => {},
      });
    } else {
      clearActivePromoterRate();
    }
  }, [
    exchangeData,
    currentAmount,
    rateReference,
    isReverse,
    enterpriseId,
    currency,
    clientCurrency,
    promoterId,
    createRateAndCalculate,
    activeRateUuid,
    onRateClear,
    clearActivePromoterRate,
    recalculateWithCurrentRate,
  ]);

  const handleRateCreate = useCallback(
    async (rate: number, cleanedValue: string) => {
      if (!rateReference || !promoterId || currentAmount === '0') return;
      if (!exchangeData) return;

      const validation = validatePromoterRate(rate, exchangeData);
      if (!validation.isValid) {
        return;
      }

      await createRateAndCalculate({
        promoterId,
        originalReference: rateReference,
        promoterRate: cleanedValue,
        currentAmount,
        isReverse,
        enterpriseId,
        onCalculationResult: ({
          calculatedAmount,
          feeDescription,
          activeRateUuid: newActiveRateUuid,
        }) => {
          isUpdatingFromPromoterRef.current = true;

          onRateChange({
            rate,
            calculatedAmount: calculatedAmount.toString(),
            isReverse,
            feeDescription,
            activeRateUuid: newActiveRateUuid || activeRateUuid,
          });

          setTimeout(() => {
            isUpdatingFromPromoterRef.current = false;
          }, 0);
        },
        setCancelFunction: () => {},
      });
    },
    [
      rateReference,
      promoterId,
      currentAmount,
      isReverse,
      enterpriseId,
      exchangeData,
      createRateAndCalculate,
      onRateChange,
      activeRateUuid,
    ]
  );

  const handleRateChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
        setHookError(null);
        const { value } = e.target;
        const cleanedValue = validateDecimalInput(value);
        setRateInput(cleanedValue);
        setHasUserInteracted(true);

        if (!cleanedValue) {
          await handleRateClear();
          return;
        }

        const rate = parseValidRate(cleanedValue, exchangeData);
        if (!rate) return;

        if (currentAmount && currentAmount !== '0') {
          await handleRateCreate(rate, cleanedValue);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unexpected error occurred';
        setHookError(errorMessage);
      }
    },
    [handleRateClear, handleRateCreate, currentAmount, exchangeData]
  );

  return {
    rateInput,
    hasUserInteracted,
    isCurrentRateValid,
    baseValidationMessage,
    alertProps,
    handleRateChange,
    hookError,
  };
};

export default usePromoterRateInput;
